import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { ARTIFACT_PAGE_LIMIT, useArtifactDetail, useArtifactPage } from "@/api/artifacts";
import { ApiError } from "@/api/client";
import {
  ACTIVE_RUN_STATES,
  type RunOutputArtifact,
  runServedFromCache,
  useRun,
  useRunProvenance,
} from "@/api/runs";
import { EmptyState } from "@/components/layout/EmptyState";
import { RunWorkspace } from "@/components/runs/RunWorkspace";
import { RunArtifactCard } from "@/components/runs/RunArtifactCard";
import { RunHeader } from "@/components/runs/RunHeader";
import { RunLifecycle } from "@/components/runs/RunLifecycle";
import { RunLogs } from "@/components/runs/RunLogs";
import { RunProvenance } from "@/components/runs/RunProvenance";
import { RunScratch } from "@/components/runs/RunScratch";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { absentTimingLabel } from "@/components/runs/run-timing";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Lazy-loaded so recharts (~380 KB) only lands when a user opens the
// Analysis tab, not on every route (flowstack#992). This page was ONE of
// three static edges from a route into `recharts` — the others being
// `AnalysisRunView` (the same tab, standalone) and `RunWorkspace` →
// `RunTorsionPlots` — and a single remaining static edge is enough to put
// the library back in the entry chunk, which is why all three moved
// together. Same rationale + pattern as `RunWorkspace`'s `DAGCanvas`.
const AnalysisTab = React.lazy(() =>
  import("@/components/runs/AnalysisTab").then((m) => ({ default: m.AnalysisTab }))
);

interface RouteParams {
  run_id?: string;
  [key: string]: string | undefined;
}

/**
 * Per-Tile run detail page — 5 tabs (Overview/Inputs/Outputs/Logs/
 * Provenance) over the rebuilt design-system primitives.
 *
 * Polls every 5s while the run is active (handled by useRun's
 * ETag-aware client wrapper + TanStack Query staleTime). Inputs
 * and Outputs are stubs until the catalog returns per-port detail;
 * Logs and Provenance are wired against the B1 stubbed endpoints.
 */
export function RunDetailPage(): JSX.Element {
  const { run_id = "" } = useParams<RouteParams>();
  const { data: run, isLoading, isError, error } = useRun(run_id);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-5 w-1/4" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !run) {
    const isNotFound = error instanceof ApiError && error.status === 404;
    return (
      <EmptyState
        title={isNotFound ? "Run not found." : "Could not load run."}
        description={
          isNotFound
            ? `No run with id ${run_id ? `'${run_id}'` : "(empty)"}.`
            : error instanceof Error
              ? error.message
              : "Unknown error."
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <RunHeader run={run} />

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {/* Only a Flow-linked run has a graph to show (flowstack#625) —
           * a standalone Tile run has no DAG tab at all rather than an
           * empty/N-A one. */}
          {run.flow_name && <TabsTrigger value="dag">DAG</TabsTrigger>}
          <TabsTrigger value="inputs">Inputs</TabsTrigger>
          <TabsTrigger value="outputs">Outputs</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="scratch">Raw scratch</TabsTrigger>
          <TabsTrigger value="provenance">Provenance</TabsTrigger>
          <TabsTrigger value="analysis">Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <section className="flex flex-col gap-4">
            {/* VIS/V1 — the live run state, above the static rollup because a
                run in flight is what the reader came for. It renders NOTHING
                once the run reaches a terminal label (see `RunLifecycle`), so
                a settled run's Overview is exactly what it was before this
                unit: no second, contradicting tile list on the page. */}
            <RunLifecycle run={run} />

            <Card className="p-card">
              <div className="flex items-center justify-between gap-4">
                <h2 className="font-display text-xl font-semibold tracking-tight">Overview</h2>
                {/* flowstack#1941: every run this page can load is a row of the
                    runs table, so one carrying a flow name is a STEP of a flow
                    run, never the flow run itself. "Flow run" here put the
                    flow's label on a single node's run. */}
                <span className="label-micro rounded-full border border-border-subtle bg-surface-2 px-2 py-0.5 text-muted-foreground">
                  {run.flow_name ? "Flow node run" : "Tile run"}
                </span>
              </div>
              <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[180px_1fr]">
                <dt className="label-micro text-muted-foreground">State</dt>
                <dd>
                  <RunStateBadge state={run.status} size="md" />
                </dd>
                <dt className="label-micro text-muted-foreground">Submitted</dt>
                <dd className="text-sm">
                  {new Date(run.submitted_at).toLocaleString()}{" "}
                  <span className="text-muted-foreground">by {run.submitted_by}</span>
                </dd>
                {run.tile_name && (
                  <>
                    <dt className="label-micro text-muted-foreground">Tile</dt>
                    <dd className="font-mono text-sm">{run.tile_name}</dd>
                  </>
                )}
                {run.flow_node_name && (
                  <>
                    <dt className="label-micro text-muted-foreground">Node</dt>
                    <dd className="font-mono text-sm">{run.flow_node_name}</dd>
                  </>
                )}
                {run.flow_run_id && (
                  <>
                    <dt className="label-micro text-muted-foreground">In flow run</dt>
                    <dd className="text-sm">
                      {/* No flow-run page exists yet; the Runs list filtered to
                          the id shows exactly this submission's steps. */}
                      <Link
                        to={`/runs?q=${encodeURIComponent(run.flow_run_id)}`}
                        className="font-mono underline-offset-2 hover:underline"
                      >
                        {run.flow_run_id}
                      </Link>
                      {run.flow_name && (
                        <span className="text-muted-foreground"> · {run.flow_name}</span>
                      )}
                    </dd>
                  </>
                )}
                <dt className="label-micro text-muted-foreground">Invocations</dt>
                <dd className="text-sm">
                  {run.invocations.length} {run.invocations.length === 1 ? "row" : "rows"}
                </dd>
              </dl>
            </Card>

            {run.invocations.length === 0 ? (
              <EmptyState
                title="No invocations yet."
                description="Tile invocations appear here once the Flow runner expands the DAG and dispatches the child runs."
              />
            ) : (
              <Card className="p-2">
                <Table role="table">
                  <TableCaption>Tile invocations for this run</TableCaption>
                  <TableHeader>
                    <TableRow>
                      {run.flow_name && <TableHead>Node</TableHead>}
                      <TableHead>Tile</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Cache</TableHead>
                      <TableHead>Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {run.invocations.map((inv) => (
                      <TableRow key={inv.invocation_id} interactive="hover">
                        {run.flow_name && (
                          <TableCell className="font-mono text-xs">
                            {inv.node_name ?? "—"}
                          </TableCell>
                        )}
                        <TableCell className="font-mono text-xs">{inv.tile_name}</TableCell>
                        <TableCell>
                          <RunStateBadge state={inv.status} />
                        </TableCell>
                        <TableCell className="text-xs">{inv.cache_hit ? "hit" : "miss"}</TableCell>
                        {/* flowstack#1270: a null duration says WHY it is
                            null. The bare em dash that stood here is
                            indistinguishable from a run that took no time,
                            and this column is the one an operator reads to
                            answer "how long did this take". */}
                        <TableCell className="font-mono tabular-nums">
                          {inv.duration_seconds !== null
                            ? `${inv.duration_seconds.toFixed(1)}s`
                            : absentTimingLabel(inv.status, inv.cache_hit)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </section>
        </TabsContent>

        {run.flow_name && (
          <TabsContent value="dag">
            <RunWorkspace runId={run.run_id} />
          </TabsContent>
        )}

        <TabsContent value="inputs">
          <EmptyState
            title="No input ports yet."
            description="Per-port artifact metadata appears here once submission writes attach typed-object inputs to the run."
          />
        </TabsContent>

        <TabsContent value="outputs">
          {runServedFromCache(run) ? (
            <CachedRunOutputsPanel
              runId={run.run_id}
              isActive={ACTIVE_RUN_STATES.has(run.status)}
            />
          ) : (
            <RunOutputsPanel runId={run.run_id} isActive={ACTIVE_RUN_STATES.has(run.status)} />
          )}
        </TabsContent>

        <TabsContent value="logs">
          <RunLogs runId={run.run_id} isActive={ACTIVE_RUN_STATES.has(run.status)} />
        </TabsContent>

        <TabsContent value="scratch">
          <RunScratch runId={run.run_id} />
        </TabsContent>

        <TabsContent value="provenance">
          <RunProvenance runId={run.run_id} isActive={ACTIVE_RUN_STATES.has(run.status)} />
        </TabsContent>

        <TabsContent value="analysis">
          <React.Suspense fallback={<Skeleton className="h-64 w-full" />}>
            <AnalysisTab runId={run.run_id} />
          </React.Suspense>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Outputs tab — the artifacts this run produced.
 *
 * Asks the server for `run_id=<this run>` (flowstack#963) rather than pulling
 * the global feed and filtering it here. The old shape could not answer this
 * question for an older run at all: the feed was capped, so a run whose
 * outputs fell outside that window rendered "No outputs yet" over artifacts
 * that plainly existed.
 *
 * "Not finished yet" is said only while the run is in flight (flowstack#1942):
 * on a settled run it is false, whatever the reason the list is empty.
 */
function RunOutputsPanel({ runId, isActive }: { runId: string; isActive: boolean }): JSX.Element {
  const { data, isLoading } = useArtifactPage({ runId, limit: ARTIFACT_PAGE_LIMIT });

  if (isLoading) return <OutputsSkeleton />;

  const owned = data?.artifacts ?? [];
  if (owned.length === 0) {
    return isActive ? (
      <EmptyState
        title="No outputs yet."
        description="Output artifacts appear here once the Tile body completes and registers them with the catalog."
      />
    ) : (
      <EmptyState
        title="No outputs."
        description="This run settled without registering any output artifacts with the catalog."
      />
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {owned.map((art) => (
        <RunArtifactCard key={art.artifact_id} artifact={art} />
      ))}
    </div>
  );
}

/**
 * Outputs tab for a run SERVED from the cache (flowstack#1942).
 *
 * A cache hit registers no artifacts of its own: the catalog attributes each
 * one to the earlier run that produced it, so the `run_id` feed above is empty
 * by construction and used to read "No outputs yet" on a completed run. What
 * the hit DID record is its `run_outputs` rows — the artifacts it was served —
 * which the provenance bundle lists as `output_artifacts`.
 *
 * A hit can still be in the active `cached` state with its `run_outputs` rows
 * not yet written, so the list polls while `isActive`. A failed refetch keeps
 * the last good list on screen and says it may be stale.
 */
function CachedRunOutputsPanel({
  runId,
  isActive,
}: {
  runId: string;
  isActive: boolean;
}): JSX.Element {
  const { data, isLoading, isError } = useRunProvenance(runId, isActive);

  if (isLoading) return <OutputsSkeleton />;
  if (isError && !data) {
    return (
      <EmptyState
        title="Could not load outputs."
        description="This run was served from cache, and the list of outputs it was served could not be fetched. Retry, or check the Provenance tab."
      />
    );
  }

  const outputs = data?.output_artifacts ?? [];
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground" data-testid="outputs-served-from-cache">
        Served from cache: this run reused the outputs of an earlier run with the same cache key and
        did not execute.
      </p>
      {isError && (
        // No warning token exists, and `--state-*` means a lifecycle fact: a
        // refetch failure is chrome, so it takes full-contrast body ink and
        // weight to stand above the muted note beside it.
        <p
          className="text-sm font-medium text-foreground"
          role="status"
          data-testid="outputs-stale-warning"
        >
          Could not refresh the outputs; this list may be out of date.
        </p>
      )}
      {outputs.length === 0 ? (
        isActive ? (
          <EmptyState
            title="No outputs recorded yet."
            description="The outputs this run is served appear here once they are recorded."
          />
        ) : (
          <EmptyState
            title="No outputs recorded."
            description="This run was served from cache but recorded no output rows."
          />
        )
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {outputs.map((output) => (
            <CachedOutputCard key={`${output.port}-${output.ordinal}`} output={output} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One served output: the catalog row behind its hash, or a plain link to it
 * while that row is loading or could not be read. */
function CachedOutputCard({ output }: { output: RunOutputArtifact }): JSX.Element {
  const { data, isLoading } = useArtifactDetail(output.content_hash);
  if (data) return <RunArtifactCard artifact={data} />;
  if (isLoading) return <Skeleton className="h-24 w-full" />;
  return (
    <Card className="p-card">
      <p className="text-xs text-muted-foreground">{output.port}</p>
      <Link
        to={`/artifacts/${encodeURIComponent(output.content_hash)}`}
        className="block truncate font-mono text-xs hover:underline"
        title={output.content_hash}
      >
        {output.content_hash}
      </Link>
    </Card>
  );
}

function OutputsSkeleton(): JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-24 w-full" />
      ))}
    </div>
  );
}
